import { Business, Service, ServiceSellCost, DEFAULT_CURRENCY, PRICE_BY_OPTIONS } from "@turizi/shared";
import { useTranslation } from 'react-i18next';
import { Button, LoadingOverlay, Select, Switch } from '@mantine/core';
import { IconTrash, IconArrowUp, IconArrowDown, IconCalendar, IconBuildingStore, IconReceipt2 } from '@tabler/icons-react';
import { useState, useMemo, useCallback, memo } from 'react';
import api from '@/services/api-service';
import { formatDay, formatPrice, todayStart } from "@/lib/format-utils";
import ServiceNewCost from "./new-cost-section";
import { useDeleteServiceSellCost, useUpdateService } from "@/hooks/api-hooks";
import dayjs from 'dayjs';
import EditSingleCost from "./edit-single-cost";
import { TagIcon } from "@/lib/tag-utils";

const isActiveNow = (item: ServiceSellCost) => {
    const today = todayStart();
    if (!item.start_day && !item.end_day) return true;
    if (!item.start_day) return item.end_day ? item.end_day >= today : true;
    if (!item.end_day) return item.start_day <= today;
    return item.start_day <= today && item.end_day >= today;
};

const isPast = (date: string | null | undefined) => {
    if (!date) return false;
    const today = todayStart();
    return dayjs(date).isBefore(today, 'day');
};

const isUpcoming = (startDate: string | null | undefined) => {
    if (!startDate) return false;
    const today = todayStart();
    return dayjs(startDate).isAfter(today, 'day');
};

export type Props = {
    service: Service;
};

const EditServiceCosts = memo(function EditServiceCostsTable({ service }: Props) {
    const { t } = useTranslation();
    const [error, setError] = useState<any>(null);
    const [costBy, setCostBy] = useState<typeof PRICE_BY_OPTIONS[number]['value']>(service.cost_by);
    const [sortBy, setSortBy] = useState<'date' | 'provider' | 'default'>('date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const handleSort = useCallback((column: 'date' | 'provider' | 'default') => {
        setSortBy(prevSortBy => {
            if (prevSortBy === column) {
                setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
                return prevSortBy;
            } else {
                setSortOrder('asc');
                return column;
            }
        });
    }, []);

    // Sort items based on current sort settings
    const sortedItems = useMemo(() =>
        [...(service.sell_costs || [])].sort((a, b) => {
            if (sortBy === 'date') {
                if (!a.start_day && !b.start_day) return 0;
                if (!a.start_day) return sortOrder === 'asc' ? 1 : -1;
                if (!b.start_day) return sortOrder === 'asc' ? -1 : 1;
                const comparison = a.start_day < b.start_day ? -1 : 1;
                return sortOrder === 'asc' ? comparison : -comparison;
            } else if (sortBy === 'provider') {
                const aName = a.business?.name || t('all_providers');
                const bName = b.business?.name || t('all_providers');
                const comparison = aName.localeCompare(bName);
                return sortOrder === 'asc' ? comparison : -comparison;
            } else {
                // Sort by default cost amount
                const aAmount = a.default_amount || 0;
                const bAmount = b.default_amount || 0;
                const comparison = aAmount - bAmount;
                return sortOrder === 'asc' ? comparison : -comparison;
            }
        })
        , [service.sell_costs, sortBy, sortOrder, t]);

    const deleteCostMutation = useDeleteServiceSellCost();
    const updateServiceMutation = useUpdateService();
    const seniorLabel = service.show_retired ? t('retired_plural') : t('seniors');

    const handleRemove = (item: ServiceSellCost) => {
        if (item.id) {
            deleteCostMutation.mutate(item.id);
        }
    };

    const handleCostByChange = async (value: string) => {
        const { error } = await api.services.save({ id: service.id, cost_by: value });
        if (error) {
            setError(error);
        }
    };

    const handleShowRetiredChange = (checked: boolean) => {
        updateServiceMutation.mutate({ id: service.id, show_retired: checked });
    };

    return (
        <div className="space-y-4">
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                </div>
            )}

            <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-wrap items-end gap-4">
                    <Select
                        error={!costBy}
                        className="font-bold w-fit"
                        label={t('cost_by')}
                        value={costBy}
                        data={PRICE_BY_OPTIONS.map(option => ({
                            label: t(option.label),
                            value: option.value,
                        }))}
                        onChange={(value) => {
                            if (value) {
                                setCostBy(value as any);
                                handleCostByChange(value);
                            }
                        }}
                    />
                    <div className="pb-1.5">
                        <Switch
                            size="xs"
                            color="black"
                            checked={Boolean(service.show_retired)}
                            onChange={(e) => handleShowRetiredChange(e.currentTarget.checked)}
                            label={t('show_retired')}
                        />
                    </div>
                </div>
                <ServiceNewCost
                    service={service}
                    onSave={() => { }}
                    onCancel={() => { }}
                />
            </div>

            <div className="relative">
                <LoadingOverlay visible={deleteCostMutation.isPending} />

                {/* Toolbar: Counter & Sort controls */}
                {sortedItems.length > 0 && (
                    <div className="flex items-center justify-between text-xs text-gray-500 pb-2">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400">{t('sort_by')}:</span>
                            <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => handleSort('date')}
                                    className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${sortBy === 'date' ? 'bg-white text-gray-900 shadow-2xs' : 'text-gray-500 hover:text-gray-900'}`}
                                >
                                    {t('date')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleSort('provider')}
                                    className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${sortBy === 'provider' ? 'bg-white text-gray-900 shadow-2xs' : 'text-gray-500 hover:text-gray-900'}`}
                                >
                                    {t('provider')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleSort('default')}
                                    className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${sortBy === 'default' ? 'bg-white text-gray-900 shadow-2xs' : 'text-gray-500 hover:text-gray-900'}`}
                                >
                                    {t('amount')}
                                </button>
                            </div>
                            <Button
                                variant="subtle"
                                color="gray"
                                size="xs"
                                className="px-1.5"
                                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                                title={sortOrder === 'asc' ? t('ascending') : t('descending')}
                            >
                                {sortOrder === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />}
                            </Button>
                        </div>
                    </div>
                )}

                {/* List of cards */}
                {sortedItems.length === 0 ? (
                    <div className="text-center py-12 px-4 rounded-xl border border-dashed border-gray-200 bg-gray-50/50">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3 text-gray-400">
                            <IconReceipt2 size={24} stroke={1.5} />
                        </div>
                        <div className="text-sm font-medium text-gray-900">{t('no_costs_configured')}</div>
                        <div className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                            {t('no_costs_description')}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {sortedItems.map((item) => {
                            const business = item.business;
                            const currency = item.default_currency || item.foreigner_currency || item.child_currency || item.senior_currency || item.infant_currency || DEFAULT_CURRENCY;
                            const isCurrent = isActiveNow(item);
                            const isPastItem = isPast(item.end_day);
                            const isFuture = isUpcoming(item.start_day);

                            const activeTiers = [
                                { key: 'default', label: t('default'), amount: item.default_amount, isPrimary: true },
                                { key: 'child', label: t('children'), amount: item.child_amount },
                                { key: 'senior', label: seniorLabel, amount: item.senior_amount },
                                { key: 'infant', label: t('infants'), amount: item.infant_amount },
                                { key: 'foreigner', label: t('foreigner'), amount: item.foreigner_amount },
                            ].filter(tier => tier.amount !== null && tier.amount !== undefined && (tier.isPrimary ? true : tier.amount > 0));

                            return (
                                <div
                                    key={item.id}
                                    className={`group flex flex-col md:flex-row md:items-center justify-between p-3.5 sm:p-4 rounded-xl border transition-all duration-150 gap-3 md:gap-4 ${isPastItem
                                            ? 'bg-gray-50/70 border-gray-200/80 opacity-60 hover:opacity-100'
                                            : isCurrent
                                                ? 'bg-white border-emerald-300 shadow-xs ring-1 ring-emerald-500/10'
                                                : 'bg-white border-gray-200 hover:border-gray-300 shadow-2xs'
                                        }`}
                                >
                                    {/* Left: Dates (Primary), Status & Provider (Secondary) */}
                                    <div className="space-y-1 min-w-[240px]">
                                        <div className="flex items-center flex-wrap gap-2">
                                            <div className="flex items-center gap-1.5 font-bold text-gray-900 text-sm sm:text-base tracking-tight">
                                                <IconCalendar size={16} className={isCurrent ? "text-emerald-600" : isPastItem ? "text-gray-400" : "text-blue-600"} stroke={2} />
                                                <span>
                                                    {item.start_day ? formatDay(item.start_day) : '—'} – {item.end_day ? formatDay(item.end_day) : '—'}
                                                </span>
                                            </div>
                                            {isCurrent ? (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xxs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                                    {t('status_active')}
                                                </span>
                                            ) : isPastItem ? (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xxs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                                                    {t('expired')}
                                                </span>
                                            ) : isFuture ? (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xxs font-medium bg-blue-50 text-blue-600 border border-blue-100">
                                                    {t('upcoming')}
                                                </span>
                                            ) : null}
                                        </div>

                                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                            {business ? (
                                                <div className="flex items-center gap-1.5">
                                                    {business.category && <TagIcon name={business.category} size="xxs" hideText />}
                                                    <span className="font-medium text-gray-700">{business.name}</span>
                                                    {business.city && <span className="text-gray-400 font-normal">({business.city})</span>}
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1 text-gray-500">
                                                    <IconBuildingStore size={13} stroke={1.5} className="text-gray-400" />
                                                    <span className="font-medium text-gray-600">{t('all_providers')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Middle: Costs Breakdown (Currency displayed ONLY ONCE at the end) */}
                                    <div className="flex flex-wrap items-center gap-2 md:justify-end flex-1">
                                        {activeTiers.length === 0 ? (
                                            <span className="text-xs text-gray-400 italic">{t('no_rates_defined')}</span>
                                        ) : (
                                            <>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {activeTiers.map(tier => (
                                                        <div
                                                            key={tier.key}
                                                            className={`flex items-baseline gap-1.5 px-2.5 py-1 rounded-lg text-xs border ${tier.isPrimary
                                                                    ? 'bg-gray-900 border-gray-900 text-white shadow-2xs'
                                                                    : 'bg-gray-50 border-gray-200/90 text-gray-800'
                                                                }`}
                                                        >
                                                            <span className={tier.isPrimary ? 'text-gray-300 text-xxs font-semibold uppercase tracking-wider' : 'text-gray-500 text-xxs font-medium uppercase tracking-wider'}>
                                                                {tier.label}
                                                            </span>
                                                            <span className={`font-bold tabular-nums ${tier.isPrimary ? 'text-white text-sm' : 'text-gray-900 text-xs'}`}>
                                                                {formatPrice(tier.amount)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-bold tracking-wide border border-gray-200 uppercase">
                                                    {currency}
                                                </span>
                                            </>
                                        )}
                                    </div>

                                    {/* Right: Actions */}
                                    <div className="flex items-center gap-1 justify-end md:justify-start shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-gray-100">
                                        <EditSingleCost
                                            cost={item}
                                            service={service}
                                        />
                                        <Button
                                            color="red"
                                            size="xs"
                                            variant="subtle"
                                            onClick={() => handleRemove(item)}
                                        >
                                            <IconTrash size={14} stroke={1.5} />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
});

export default EditServiceCosts;